import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { PageMetaService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { fadeIn, fadeOut, slideIn, slideOut } from '../../../../../../custom/animation/custom-animations';
import { ICON_TYPE } from '../../../../../../custom/icons/icon.model';
import { CustomInvoiceHistoryService } from '../facade/custom-invoice-history.service';
import { B2BDocumentTypeList, InvoiceHistoryList, InvoicesFilter } from '../invoices.model';
import { StateWithInvoices } from '../store/selectors/custom-invoice.selector';

@Component({
  selector: 'app-custom-invoice-history',
  templateUrl: './custom-invoice-history.component.html',
  styleUrls: ['./custom-invoice-history.component.scss'],
  animations: [fadeIn, fadeOut, slideIn, slideOut],
  standalone: false
})
export class CustomInvoiceHistoryComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  pageTitle: string;
  pageDescription: string;
  sortType: string;
  iconTypes = ICON_TYPE;
  showAdvancedSearch = false;
  userId: string;
  appliedFilter = false;
  filtersForm: UntypedFormGroup = this.fb.group({
    documentType: [''],
    orderCode: [''],
    iniDate: [''],
    endDate: ['']
  });

  loadingState$: Observable<boolean> = this.invoiceHistoryService.getInvoicesHistoryLoaderState();
  private PAGE_SIZE = 20;
  invoices$: Observable<InvoiceHistoryList> = this.invoiceHistoryService
    .getInvoicesHistoryList(this.PAGE_SIZE)
    .pipe(
      tap((invoices: InvoiceHistoryList) => {
        if (invoices.pagination) {
          this.sortType = invoices.pagination.sort;
        }
      })
    );

  constructor(
    protected invoiceHistoryService: CustomInvoiceHistoryService,
    protected store: Store<StateWithInvoices>,
    protected fb: UntypedFormBuilder,
    protected pageMetaService: PageMetaService,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.pageMetaService.getMeta().subscribe(
        data => {
          this.pageTitle = data.title;
          this.pageDescription = data.description;
        },
      ),
    );
    this.invoiceHistoryService.loadDocumentTypeList();
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  updateDatepicker(filterKey: string): void {
    this.filtersForm.get(filterKey)?.updateValueAndValidity();
  }

  applyFilter(): void {
    this.appliedFilter = true;
    this.fetchInvoices({ sortCode: null, currentPage: 0 });
  }

  patchDateValue(format: string): InvoicesFilter {
    const formIniDate = this.filtersForm.get('iniDate').value;
    const formEndDate = this.filtersForm.get('endDate').value;
    let iniDate;
    let endDate;

    if (formIniDate) {
      iniDate = formatDate(formIniDate, format, 'es-ES');
    }

    if (formEndDate) {
      endDate = formatDate(formEndDate, format, 'es-ES');
    }

    return {
      ...this.filtersForm.value,
      iniDate,
      endDate
    };
  }

  pageChange(page: number): void {
    const event: { sortCode: string; currentPage: number } = {
      sortCode: this.sortType,
      currentPage: page,
    };
    this.fetchInvoices(event);
  }

  getDocumentTypeList(): Observable<B2BDocumentTypeList> {
    return this.invoiceHistoryService.getDocumentTypeList();
  }

  private fetchInvoices(event: { sortCode: string; currentPage: number }): void {
    const formattedForm = this.patchDateValue('dd/MM/yyyy');
    this.invoiceHistoryService.loadInvoiceList(
      this.PAGE_SIZE,
      event.currentPage,
      event.sortCode,
      formattedForm,
    );
    this.patchDateValue('yyyy-MM-dd');
  }
}
